// @flow

import {KB_ARTICLES_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {
  get,
  del,
  createErrorHandler,
  postJson,
  patchJson,
  showForbiddenMessageHandler,
} from 'app/utils/request';
import {UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import {limitRequests} from 'app/utils/redux';
import type {ResponseType} from 'app/utils/redux_v2';
import type {KbQuestionType, KBCategoriesFetchParamsType} from 'app/kbs/types';
import {showNotification} from 'app/toasts/slice';

/* == KBActionType should contain all action types ==*/

export type KBQuestionActionType =
  | ReceiveKBQuestionsListType
  | ReceiveKBQuestionDetailActionType
  | ReceiveKBArticleType;

/* ==== fetch kb articles === */

export const RECEIVE_KB_ARTICLES_LIST = 'RECEIVE_KB_ARTICLES_LIST';

type ReceiveKBQuestionsListType = {
  type: 'RECEIVE_KB_ARTICLES_LIST',
  query: KBCategoriesFetchParamsType,
  response: ResponseType<KbQuestionType>,
};
export const ReceiveKBQuestionsList = (
  query: KBCategoriesFetchParamsType,
  response: ResponseType<KbQuestionType>
): ReceiveKBQuestionsListType => ({
  type: RECEIVE_KB_ARTICLES_LIST,
  query,
  response,
});

const requestKBQuestions = (params: KBCategoriesFetchParamsType) =>
  get(KB_ARTICLES_API_URL, params);

export const fetchKBQuestions =
  (params: KBCategoriesFetchParamsType) => (dispatch: SimpleDispatch) =>
    requestKBQuestions(params).then(
      (response) => dispatch(ReceiveKBQuestionsList(params, response)),
      createErrorHandler(gt('Unable to load the knowledge bases'), dispatch)
    );

export const fetchKBQuestionsForTree = limitRequests(
  (kb_id: number, category_id: null | number) => (dispatch: SimpleDispatch) => {
    return dispatch(
      fetchKBQuestions({kb_id, category_id, page_size: UNLIMITED_PAGE_SIZE})
    );
  }
);

/* ==== fetch a single kb article === */

export const RECEIVE_KB_ARTICLE_DETAIL = 'RECEIVE_KB_ARTICLE_DETAIL';

type ReceiveKBArticleType = {
  type: 'RECEIVE_KB_ARTICLE_DETAIL',
  article: KbQuestionType,
};

export const receiveKBArticle = (
  article: KbQuestionType
): ReceiveKBArticleType => ({
  type: RECEIVE_KB_ARTICLE_DETAIL,
  article,
});

export const fetchKBArticle =
  (articleId: number) => (dispatch: SimpleDispatch) =>
    get(`${KB_ARTICLES_API_URL}${articleId}`).then(
      (article) => dispatch(receiveKBArticle(article)),
      createErrorHandler(gt('Unable to load the article'), dispatch)
    );

/* === create kb question === */

export const createNewKBQuestion =
  (data: $Shape<KbQuestionType>) => (dispatch: SimpleDispatch) =>
    // https://twitter.com/dan_abramov/status/770914221638942720
    postJson(KB_ARTICLES_API_URL, data).then(
      (question: KbQuestionType) =>
        dispatch(ReceiveKBQuestionDetail(question)).question,
      createErrorHandler(
        gt('Could not create the Knowledge Base Question.'),
        dispatch
      )
    );

/* === fetch single kb question === */

export const RECEIVE_KB_QUESTION_DETAIL = 'RECEIVE_KB_QUESTION_DETAIL';

type ReceiveKBQuestionDetailActionType = {
  type: 'RECEIVE_KB_QUESTION_DETAIL',
  question: KbQuestionType,
};

const ReceiveKBQuestionDetail = (
  question: KbQuestionType
): ReceiveKBQuestionDetailActionType => ({
  type: RECEIVE_KB_QUESTION_DETAIL,
  question,
});

/* === update kb question === */

export const updateKBQuestion =
  (url: string, data: $Shape<KbQuestionType>) => (dispatch: SimpleDispatch) =>
    patchJson(url, data).then(
      (kb) => dispatch(ReceiveKBQuestionDetail(kb)),
      createErrorHandler(
        gt('Unable to update the Knowledge Base Question'),
        dispatch,
        showForbiddenMessageHandler(true)
      )
    );

export const deleteKBQuestion =
  (question: KbQuestionType) => (dispatch: SimpleDispatch) =>
    del(question.url).then(
      () =>
        dispatch(
          showNotification(gt('Question %s has been deleted.', question.name))
        ),
      createErrorHandler(
        gt('Unable to delete the question'),
        dispatch,
        showForbiddenMessageHandler(true)
      )
    );
